<template>
	<header class="header">
		<router-link class="logo favicon" to="/">{{ siteName }}</router-link>

		<nav :class="{ menu: true, mini: !fullMenu }">
			<div :class="{ 'action-menu': true, 'feature-only': isFeatureOnly }" v-if="!isPublic">
				<div class="menu-item menu-item-dashboard">
					<router-link to="/" exact @click="menuLinkClick">
						<Icon type="dashboard" class="svg" />
						<span class="new-count" v-if="newCount.dashboard" v-html="newCount.dashboard" />
						<span class="label">Dashboard</span>
					</router-link>
				</div>

				<div class="menu-item menu-item-opportunities">
					<router-link to="/opportunities" exact @click="menuLinkClick">
						<Icon type="pitch" class="svg" />
						<span class="label">Opportunities</span>
					</router-link>
				</div>

				<div class="menu-item" v-if="!isFeatureOnly">
					<router-link to="/matches" exact @click="menuLinkClick">
						<Icon type="matches" class="svg" />
						<span class="new-count" v-if="newCount.matches" v-html="newCount.matches" />
						<span class="label">Matches</span>
					</router-link>
				</div>

				<div class="menu-item menu-item-requests" v-if="showRequests">
					<router-link :to="newCount.requests ? '/requests/received' : '/requests'" exact @click="menuLinkClick">
						<Icon type="requests" class="svg" />
						<span class="new-count" v-if="newCount.requests" v-html="newCount.requests" />
						<span class="label" v-html="isFeatureOnly ? 'Pitches Received' : 'Sent/Received'" />
					</router-link>
				</div>

				<div class="menu-item" v-if="!isFeatureOnly">
					<router-link :to="newCount.connections ? '/connections/connected' : '/connections'" exact @click="menuLinkClick">
						<Icon type="connections" class="svg" />
						<span class="label">Connections</span>
					</router-link>
				</div>

				<div class="menu-item" v-if="!isFeatureOnly">
					<router-link to="/declines" exact @click="menuLinkClick">
						<Icon type="declines" class="svg" />
						<span class="label">Declines</span>
					</router-link>
				</div>

				<div class="menu-item" v-if="!isFeatureOnly">
					<router-link to="/inspiration" exact @click="menuLinkClick">
						<Icon type="ideas" class="svg" />
						<span class="label">Collab Ideas</span>
					</router-link>
				</div>

				<div class="menu-item" v-if="!isFeatureOnly">
					<span @click="loadVideos">
						<Icon type="video-youtube" class="svg" />
						<span class="label">Video Guides</span>
					</span>
				</div>

				<div class="menu-item menu-item-profile">
					<router-link
						:to="!isFeatureOnly ? '/account/brands' : '/account/profile/'"
						@click="menuLinkClick"
						:class="{ active: $route.name === 'account' }">
						<Icon type="profile" class="svg" />
						<span class="label">Profile</span>
					</router-link>
				</div>

				<div class="menu-item minmax">
					<span @click="showFullMenu">
						<Icon type="show-menu" class="svg" />
						<span class="label">Mini Menu</span>
					</span>
				</div>
			</div>

			<div class="admin-menu">
				<div class="admin-menu-wrap">
					<button
						type="button"
						class="book-call-header hide-mobile"
						@click="handleBookCallClick"
						v-if="!isPublic && !featureOnly && showBookCall">
						<Icon type="phone" class="svg" />
						<span class="label">Book a call</span>
					</button>

					<Icon
						type="alert"
						:titleLeft="true"
						class="svg notifications hide-mobile"
						titleFancy="Manage notifications"
						@clicked="showNotificationsPupup({ sources: [], title: 'Manage notifications' })"
						v-if="!isPublic && !featureOnly" />

					<div class="menu-item account" v-if="!isPublic">
						<span
							:title="activeBrandLabel"
							class="initials"
							:class="{ 'is-logo': !!activeBrandLogo, white: !!activeBrandLogoIsWhite, 'can-open-menu': true }"
							@click="showMenuPopout($event)">
							<img v-if="activeBrandLogo" :src="activeBrandLogo" :alt="activeBrandLabel + ' logo'" />
							<span v-else v-html="initials" />
						</span>
					</div>

					<div class="name-info" v-if="!isPublic">
						<span :class="{ active: shouldShowMenuPopout, name: true }" @click="showMenuPopout($event)">{{ activeBrandLabel }}</span>
						<span class="brand-switch-help hide-mobile" v-if="showUserBrandsHelp">Switch brands &gt;&gt;&gt;</span>

						<div
							:class="{ 'menu-popout': true, show: shouldShowMenuPopout }"
							@mouseleave="handleMouseLeave"
							@mouseenter="handleMouseEnter">
							<div
								class="link-wrap brand-link-wrap"
								v-for="userBrand in switchableUserBrands"
								:key="userBrand.id"
								@click="switchBrand(userBrand.id)">
								<span class="menu-link brand-menu-link">
									<span class="brand-menu-logo" :class="{ white: !!userBrand.brand_logo_is_white }">
										<img v-if="userBrand.brand_logo_192" :src="userBrand.brand_logo_192" :alt="userBrand.label + ' logo'" />
										<span v-else>{{ brandInitials(userBrand.label) }}</span>
									</span>
									<span class="label">{{ userBrand.label }}</span>
								</span>
							</div>
							<div class="link-wrap hide-mobile">
								<router-link :to="isFeatureOnly ? '/account/profile/' : '/account/brands'"
									><Icon type="profile" class="svg" /><span class="label">Profile</span></router-link
								>
							</div>
							<div class="link-wrap hide-tablet" v-if="showBookCall" @click="handleBookCallClick(true)">
								<span> <Icon type="phone" class="svg" /><span class="label">Book a call</span> </span>
							</div>
							<div
								class="link-wrap hide-tablet"
								@click="
									showNotificationsPupup({ sources: [], title: 'Manage notifications' });
									toggleMenu();
								">
								<span> <Icon type="alert" class="svg" /><span class="label">Notifications</span> </span>
							</div>
							<div class="link-wrap" v-if="showRequests">
								<router-link to="/account/subscriptions/"
									><Icon type="subscriptions" class="svg" /><span class="label">Billing</span></router-link
								>
							</div>
							<div class="link-wrap">
								<span @click="logout($event)" class="menu-link"
									><Icon type="sign-out" class="svg" /><span class="label">Log out</span></span
								>
							</div>
						</div>
					</div>
				</div>
			</div>
		</nav>

		<button id="toggle-menu" @click="toggleMenu" v-if="!isPublic">
			<span class="text">Menu</span>
			<span class="bars"></span>
		</button>

		<button
			type="button"
			class="mobile-brand-logo"
			:class="{ 'is-logo': !!activeBrandLogo, white: !!activeBrandLogoIsWhite }"
			@click="toggleMenu"
			v-if="!isPublic"
			:title="activeBrandLabel">
			<img v-if="activeBrandLogo" :src="activeBrandLogo" :alt="activeBrandLabel + ' logo'" />
			<span v-else>{{ brandInitials(activeBrandLabel) || initials }}</span>
		</button>
		<span class="mobile-brand-help" v-if="!isPublic && showUserBrandsHelp">Switch brands &gt;&gt;&gt;</span>
	</header>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useVueInstance } from "../composables/useVueInstance";
import { TokenService } from "./../services/storage.service";
import ApiService from "./../services/api.service";
import Icon from "./Display/BaseIcon.vue";
import general from "./../utils/general";

export default {
	name: "AppHeader",
	props: {
		siteName: {
			type: String,
			required: true,
		},
		fullMenu: {
			type: Boolean,
			default: true,
		},
	},
	components: {
		Icon,
	},
	emits: ["mobileMenu", "menuLinkClick", "showHelp", "fullMenu"],
	setup(props, { emit }) {
		const { store, route } = useVueInstance();

		// Data
		const menuPopout = ref(false);
		const menuPopoutTimer = ref(null);
		const menuPopoutTimerOK = ref(false);
		const currentBrand = ref(null);

		// Vuex
		const updateVar = (payload) => store.commit("general/updateVar", payload);
		const updateBrand = (payload) => store.dispatch("general/updateBrand", payload);
		const showNotificationsPupup = (payload) => store.commit("notifications/showNotificationsPupup", payload);

		const menuCounts = computed(() => store.state.general.menuCounts);
		const userBrands = computed(() => store.state.general.userBrands);
		const userIdentity = computed(() => store.state.general.userIdentity || {});
		const brand = computed(() => store.state.general.brand);
		const showUserBrandsHelp = computed(() => store.state.general.showUserBrandsHelp);
		const featureOnly = computed(() => store.state.general.featureOnly);
		const subscription = computed(() => store.state.general.subscription);
		const showBookCall = computed(() => !!subscription.value?.bookCall?.show);

		// Computed
		const featureIsDone = computed(() =>
			!!subscription.value && !!subscription.value.featureDone && Object.keys(subscription.value.featureDone).length > 0
				? subscription.value.featureDone
				: false
		);

		const showRequests = computed(() => {
			if (isFeatureOnly.value) {
				return !!featureIsDone.value && !!featureIsDone.value.hasHadLive;
			}
			return true;
		});

		const resolvedRouteMeta = computed(() => {
			const records = route.matched || [];
			if (records.length) {
				const last = records[records.length - 1];
				return (last && last.meta) || {};
			}
			return route.meta || {};
		});

		const isPublic = computed(() => resolvedRouteMeta.value.public);
		const isFeatureOnly = computed(() => featureOnly.value);

		const newCount = computed(() => {
			let c = { dashboard: 0, matches: 0, requests: 0, connections: 0, declines: 0 };
			if (typeof menuCounts.value === "object" && !!menuCounts.value) {
				Object.keys(menuCounts.value).forEach((p) => {
					if (menuCounts.value[p] > 0) {
						c[p] = menuCounts.value[p] > 9 ? "9+" : menuCounts.value[p];
					}
				});
			}
			return c;
		});

		const initials = computed(() => userIdentity.value.initials || "");
		const name = computed(() => userIdentity.value.name || "");
		const activeBrandInfo = computed(() => {
			const activeBrandId = currentBrand.value || brand.value;

			if (!activeBrandId || !Array.isArray(userBrands.value)) {
				return null;
			}

			return userBrands.value.find((userBrand) => Number(userBrand.id) === Number(activeBrandId)) || null;
		});
		const activeBrandLogo = computed(() => activeBrandInfo.value?.brand_logo_192 || "");
		const activeBrandLogoIsWhite = computed(() => !!activeBrandInfo.value?.brand_logo_is_white);
		const activeBrandLabel = computed(() => activeBrandInfo.value?.label || "...");
		const hasMultipleUserBrands = computed(() => Array.isArray(userBrands.value) && userBrands.value.length > 1);
		const switchableUserBrands = computed(() => {
			if (!hasMultipleUserBrands.value) return [];
			return userBrands.value.filter((userBrand) => Number(userBrand.id) !== Number(currentBrand.value || brand.value));
		});

		const showAdmin = computed(() => {
			return general.isDev() || general.isAdmin();
		});

		const shouldShowMenuPopout = computed(() => !!menuPopout.value);

		// Watch
		watch(
			() => route.fullPath,
			(to, from) => {
				clearMenuPopout();
				updateVar({ varName: "showUserBrandsHelp", info: false });
				emit("mobileMenu", false);
			}
		);

		watch(currentBrand, (val) => {
			updateVar({ varName: "showUserBrandsHelp", info: false });
			if (val) {
				updateBrand(val);
			}
		});

		watch(brand, () => {
			if (currentBrand.value !== brand.value) {
				currentBrand.value = brand.value;
			}
		});

		// Methods
		const clearMenuPopoutTimer = () => {
			if (menuPopoutTimer.value) clearTimeout(menuPopoutTimer.value);
			menuPopoutTimer.value = null;
		};

		const clearMenuPopout = () => {
			menuPopout.value = false;
			menuPopoutTimerOK.value = false;
			clearMenuPopoutTimer();
		};

		const help = () => {
			clearMenuPopout();
			emit("showHelp");
		};

		const openExternal = (url) => {
			if (url) {
				window.open(url, "_blank", "noopener");
			}
		};

		const handleBookCallClick = (closeMenu = false) => {
			if (closeMenu) {
				toggleMenu();
				clearMenuPopout();
			}

			openExternal("https://calendly.com/partneruphub/30min");

			ApiService.post("users/action/dashboard-book-call-dismissed")
				.then(() => {
					updateVar({
						varName: "subscription",
						info: {
							...(subscription.value || {}),
							bookCall: {
								...(subscription.value?.bookCall || {}),
								show: false,
							},
						},
					});
				})
				.catch((err) => {
					console.error("Unable to persist dashboard book call dismissal", err);
				});
		};

		const menuLinkClick = () => {
			clearMenuPopout();
			emit("menuLinkClick");
		};

		const loadVideos = (e) => {
			e.preventDefault();
			updateVar({ varName: "showVideos", info: true });
		};

		const showFullMenu = () => {
			emit("fullMenu");
		};

		const toggleMenu = () => {
			emit("mobileMenu");
		};

		const logout = (event) => {
			event.preventDefault();
			updateVar({ varName: "mobileMenu", info: false });
			store.dispatch("auth/logout");
		};

		const setMenuPopoutTimer = () => {
			clearMenuPopoutTimer();
			if (menuPopoutTimerOK.value) {
				menuPopoutTimer.value = setTimeout(() => {
					menuPopout.value = false;
					menuPopoutTimerOK.value = false;
				}, 1500);
			}
		};

		const handleMouseLeave = () => {
			setMenuPopoutTimer();
		};

		const handleMouseEnter = () => {
			clearMenuPopoutTimer();
			menuPopoutTimerOK.value = true;
		};

		const showMenuPopout = (event) => {
			event.preventDefault();
			updateVar({ varName: "showUserBrandsHelp", info: false });
			menuPopoutTimerOK.value = true;
			setMenuPopoutTimer();
			menuPopout.value = !menuPopout.value;
		};

		const switchBrand = (brandId) => {
			currentBrand.value = brandId;
			clearMenuPopout();
			emit("mobileMenu", false);
		};

		const brandInitials = (label) => {
			if (!label) return "";
			return label
				.split(" ")
				.filter((word) => !!word)
				.slice(0, 2)
				.map((word) => word.charAt(0))
				.join("")
				.toUpperCase();
		};

		// onMounted / created
		onMounted(() => {
			let uid = TokenService.getId();
			if (uid) {
				window.appziSettings = {
					userId: uid,
					data: {
						user: uid,
					},
				};
			}
		});

		currentBrand.value = brand.value;

		return {
			currentBrand,
			isPublic,
			newCount,
			isFeatureOnly,
			featureOnly,
			showRequests,
			featureIsDone,
			loadVideos,
			showFullMenu,
			toggleMenu,
			showNotificationsPupup,
			help,
			handleBookCallClick,
			initials,
			name,
			shouldShowMenuPopout,
			showMenuPopout,
			switchBrand,
			brandInitials,
			handleMouseLeave,
			handleMouseEnter,
			logout,
			activeBrandLogo,
			activeBrandLogoIsWhite,
			activeBrandLabel,
			hasMultipleUserBrands,
			switchableUserBrands,
			menuLinkClick,
			userBrands,
			showUserBrandsHelp,
			showAdmin,
			showBookCall,
		};
	},
};
</script>

<style lang="less" scoped>
@import "./../assets/css/less/_helper.less";
@import "./../assets/css/less/_settings.less";
@keyframes text_color_change {
	from {
		color: @formDefaultColour;
	}
	to {
		color: @guidedRose;
	}
}
.disabledLink {
	pointer-events: none;
}

.book-call-header {
	display: none;
}

.header {
	border-bottom: 1px solid rgba(0, 0, 0, 0.2);
	padding: 5px 10px;
	height: 46px;
	position: fixed;
	width: 100%;
	top: 0;
	left: 0;
	background-color: @surfacePanel;
	z-index: 10;

	&.fade {
		.opacity(0.35);
	}

	.logo {
		background: url("../assets/img/partner-up-logo-brown2.svg") 0 2px no-repeat;
		background: url("../assets/img/partner-up-logo-test1.svg") 0 2px no-repeat;
		background: url("../assets/img/logo-silly.png") 0 2px no-repeat;
		background-size: 100% auto;
		padding: 0;
		margin: 0;
		width: 140px;
		height: 36px;
		font-family: "Montserrat", sans-serif;
		letter-spacing: -2px;
		font-size: 18px;
		display: inline-block;
		text-indent: -9999px;
		&.favicon {
			background: url("../assets/img/partner-up-logo-circle-brown.svg") 0 0 no-repeat;
			aspect-ratio: 1/1;
			width: auto;
			height: 36px;
			margin: 0 auto;
			display: block;
		}
	}

	.search {
		margin: 0;
		position: absolute;
		left: 90px;
		top: 15px;
		height: 35px;
		overflow: hidden;
		display: none;
		width: 300px;
	}

	#toggle-menu {
		z-index: 3;
		display: inline-block;
		background: none;
		border: none;
		cursor: pointer;
		padding: 0;
		.trans;
		color: @darkContrast;
		position: absolute;
		width: 22px;
		top: 13px;
		right: 16px;
		height: 17px;
		outline: none;
		span {
			&.text {
				display: none;
			}

			&.bars {
				background: @darkContrast none repeat scroll 0 0;
				height: 2px;
				position: absolute;
				transition: all 0.3s ease 0s;
				width: 22px;
				right: 0;
				top: 8px;
				.rounded(@radiusMd);

				&:before,
				&:after {
					background: @darkContrast none repeat scroll 0 0;
					content: "";
					display: inline-block;
					height: 2px;
					left: 0;
					position: absolute;
					transform-origin: 2.5px center 0;
					.trans;
					width: 22px;
					top: -7px;
					border-radius: @radiusMd;
				}

				&:after {
					top: 7px;
					width: 14px;
					left: 8px;
				}
			}
		}
	}

	.mobile-brand-logo {
		align-items: center;
		background-color: @formDefaultColour;
		border: none;
		color: #fff;
		cursor: pointer;
		display: flex;
		font-size: 11px;
		font-weight: @bold;
		height: 34px;
		justify-content: center;
		line-height: 1;
		overflow: hidden;
		padding: 0;
		position: absolute;
		right: 52px;
		text-align: center;
		text-transform: uppercase;
		top: 6px;
		width: 34px;
		z-index: 3;
		.rounded(@radiusPill);

		img {
			display: block;
			height: 100%;
			object-fit: contain;
			width: 100%;
		}

		&.is-logo {
			background-color: transparent;
			border: 1px solid fade(@guidedRose, 20%);
		}

		&.white {
			background-color: @guidedRose;
		}
	}

	.mobile-brand-help {
		animation: text_color_change 1s infinite alternate;
		background-color: @surfacePanel;
		color: @guidedRose;
		display: block;
		font-size: 14px;
		font-weight: @bold;
		padding: 2px 8px;
		position: absolute;
		right: 90px;
		top: 12px;
		white-space: nowrap;
		z-index: 3;
	}

	.menu {
		padding: 0;
		position: absolute;
		left: 0;
		width: 100%;
		top: 45px;
		display: none;
		background-color: @surfacePanel;
		border-bottom: 2px solid @darkContrast;
		height: calc(100vh - 45px);
		height: calc(100svh - 45px);
		overflow-x: auto;

		hr {
			margin: 0.5em 0;
		}

		.menu-item {
			margin: 0 0 5px;
			position: relative;

			> a,
			> span,
			.menu-link {
				color: @darkContrast;
				text-decoration: none;
				background-position: 10px center;
				background-repeat: no-repeat;
				display: block;
				background-size: 18px auto;
				padding: 12px 0 12px 62px;
				.trans(background-color 0.25s linear);
				position: relative;
				cursor: pointer;

				.new-count {
					background-color: fade(@formDefaultColour, 90%);
					color: #fff;
					.rounded(@radiusPill);
					font-size: 9px;
					padding: 2px 0 0;
					position: absolute;
					left: 33px;
					top: 50%;
					margin-top: -18px;
					z-index: 1;
					letter-spacing: 0;
					text-align: center;
					width: 16px;
					height: 16px;

					&.text {
						width: auto;
						padding: 2px 4px;
						margin-top: -22px;
					}
				}

				svg,
				.svg {
					width: 22px;
					height: 22px;
					position: absolute;
					left: 20px;
					top: 50%;
					margin-top: -11px;
				}

				.svg {
					width: 24px;
				}

				&:before {
					position: absolute;
					width: 4px;
					height: 100%;
					left: 0;
					top: 0;
					content: "";
					display: block;
					.rounded(0 @radiusXs @radiusXs 0);
				}

				&.router-link-active,
				&.active {
					background-color: @surfaceInput;

					&:before {
						background-color: @formDefaultColour;
					}
				}
			}

			&.account {
				.initials {
					display: none;
				}
			}

			&.last-mobile {
				margin-bottom: 0;
			}

			&.minmax {
				display: none;
			}

			.menu-sub-items {
				display: none;
			}
		}

		.name-info {
			padding: 12px 0 12px 62px;
			position: relative;
			cursor: pointer;

			svg,
			.svg {
				width: 22px;
				height: 22px;
				position: absolute;
				left: -42px;
				top: 50%;
				margin-top: -11px;
			}

			.name {
				display: none;
			}
		}

		.menu-popout {
			.link-wrap {
				position: relative;
				margin-bottom: 26px;

				&:last-child {
					margin-bottom: 15px;
				}

				a {
					text-decoration: none;
				}
			}

			.brand-link-wrap {
				margin-bottom: 26px;
				position: relative;

				> span,
				.menu-link {
					color: @darkContrast;
					display: inline-block;
					padding: 0;
					position: relative;
					text-decoration: none;
					width: auto;

					&.brand-menu-link {
						display: flex;
						align-items: center;
					}

					.label {
						color: @darkContrast;
						font-size: inherit;
						padding: 0;
						vertical-align: top;
					}
				}
			}

			.brand-menu-logo {
				align-items: center;
				background-color: @formDefaultColour;
				color: #fff;
				display: inline-flex;
				font-size: 10px;
				font-weight: @bold;
				height: 22px;
				justify-content: center;
				left: -42px;
				line-height: 1;
				margin: -11px 0 0;
				overflow: hidden;
				position: absolute;
				text-align: center;
				text-transform: uppercase;
				top: 50%;
				width: 22px;
				.rounded(@radiusPill);

				img {
					display: block;
					height: 100%;
					object-fit: contain;
					width: 100%;
				}

				&.white {
					background-color: @guidedRose;
				}
			}
		}
	}
}

body {
	.showMenu {
		.header {
			#toggle-menu {
				span {
					&.bars {
						background: none;

						&:before,
						&:after {
							top: 0;
							background: @darkContrast;
							transform-origin: 50% 50% 0;
							width: 22px;
							transform: rotate3d(0, 0, 1, 45deg);
							.rounded(@radiusMd);
						}
						&:after {
							width: 22px;
							left: 0;
							transform: rotate3d(0, 0, 1, -45deg);
						}
					}
				}
			}

			.menu {
				display: block;
				z-index: 9;
			}
		}

		.wrapper,
		.footer {
			display: none;
		}
	}
}

.subMenuClick .menu-sub-items {
	display: none !important;
}

@media screen and (min-width: 768px) {
	.help-dt {
		padding: 9px 0 0;
		display: inline-block;
		position: relative;
		margin-right: 15px;
		vertical-align: top;
		font-weight: @bold;
		color: @guidedRose;
		cursor: pointer;
		&:hover {
			text-decoration: underline;
		}
	}

	.book-call-header {
		background: none;
		border: none;
		color: @guidedRose;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin: 3px 15px 0 0;
		padding: 6px 0;
		vertical-align: top;

		.svg {
			width: 16px;
			height: 16px;
		}

		.label {
			font-weight: @bold;
			line-height: 1;
		}

		&:hover {
			text-decoration: underline;
		}
	}

	.hide-tablet {
		display: none;
	}

	.header {
		text-align: left;
		border: none;
		background-color: rgba(0, 0, 0, 0.7);
		width: 66px;
		padding: 0;
		min-height: @minHeightDesktop;
		height: 100%;
		position: absolute;
		left: 0;

		.search {
			display: block;
		}

		.logo {
			.indent;
			background: #fff url("../assets/img/partner-up-logo-brown2.svg") 10px center no-repeat;
			background-size: 94% auto;
			background: #fff url("../assets/img/partner-up-logo-test1.svg") 10px center no-repeat;
			background-size: 80% auto;
			position: fixed;
			z-index: 3;
			padding: 0;
			width: 170px;
			height: 63px;
			left: 10px;
			margin-left: 0;
			top: 0;
			&.favicon {
				position: absolute;
				height: 46px;
				top: 9px;
			}
		}

		#toggle-menu {
			display: none;
		}

		.mobile-brand-logo {
			display: none;
		}

		.mobile-brand-help {
			display: none;
		}

		.menu {
			width: auto;
			top: 0;
			padding: 60px 0 0;
			display: block;
			min-height: 100%;
			overflow: visible;
			height: auto;
			text-align: left;
			position: relative;
			z-index: 1 !important;
			border: none;
			border-right: 1px solid @borderRoseSubtle;

			.menu-item {
				margin: 4px 0 0;

				> a,
				> span,
				.menu-link {
					font-size: 9px;
					background-size: 29px auto;
					background-position: center 0;
					text-align: center;
					padding: 30px 0 0;

					svg,
					.svg {
						width: 20px;
						height: 20px;
						top: 0;
						left: 14px;
					}

					.svg {
						width: 24px;
						left: 12px;

						&.insights {
							height: 26px;
							width: 26px;
						}
					}

					.new-count {
						margin-top: -18px;
						left: 27px;
					}

					.label {
						display: none;
						text-align: left;
						padding: 15px 0 0 42px;
						font-size: 16px;
					}

					&:before {
						display: none;
					}

					&.router-link-active,
					&:hover,
					&.active {
						border: none;
						background-color: #f0f3f2; // deferred near-match: keep explicit until visual signoff
						.opacity(1);
					}

					&:hover {
						background-color: rgba(240, 243, 242, 0.5);
					}
				}

				&.profile {
					> a {
						padding-top: 32px;
					}
				}

				&.two-line {
					margin-bottom: 10px;

					> a {
						.label {
							position: relative;
							top: -9px;
							line-height: 1.2em;
						}
					}
				}

				&.clipboard {
					> a {
						padding-top: 32px;
					}
				}

				&.handshake {
					> a {
						padding-top: 23px;

						svg {
							height: 22px;
						}
					}
				}

				&.dashboard {
					> a {
						padding-top: 32px;

						&.router-link-exact-active {
							border: none;
							background-color: transparent;
							.opacity(1);
						}
					}
				}

				&.forms {
					> a {
						padding-top: 33px;
					}
				}

				&.help {
					margin-bottom: 5px;

					> a {
						background-size: 34px auto;
						padding: 0;
						background-position: center center;
					}
				}

				&.emails {
					> a {
						background-size: 26px auto;
						background-position: center 0;
						padding-top: 30px;
					}
				}

				&.archives {
					> a {
						background-size: 26px auto;
						background-position: center 0;
						padding-top: 30px;
					}
				}

				&.matching {
					margin-top: 15px;

					> a {
						padding-top: 31px;
						background-size: 31px auto;
					}
				}

				&.minmax {
					display: block;
				}

				&:hover {
					.menu-sub-items {
						display: block;
					}
				}
			}

			&.mini {
				.menu-item {
					.label {
						position: absolute;
						background-color: @formDefaultColour;
						color: #fff;
						left: 53px;
						padding: 6px 10px;
						top: 9px;
						border-radius: @radiusXs;
						z-index: 2;
						white-space: nowrap;
						.box-shadow(1px 1px 25px -7px fade(@formDefaultColour, 50%));
						&:before {
							width: 0;
							height: 0;
							border-top: 7px solid transparent;
							border-bottom: 7px solid transparent;
							border-right: 10px solid @formDefaultColour;
							content: "";
							position: absolute;
							left: -10px;
							top: 50%;
							margin-top: -7px;
						}
					}
				}
			}

			.action-menu {
				min-height: 490px;
				padding-left: 8px;

				.menu-item {
					svg,
					.svg {
						margin-top: 15px;

						&.insights {
							margin-top: 12px;
						}
					}

					> a,
					> span,
					.menu-link {
						padding: 0;
						height: 50px;
						width: 50px;
						.rounded(@radiusMd);
					}
				}
				&.feature-only {
					display: flex;
					flex-direction: column;
					justify-content: flex-start;
					.menu-item {
						order: 5;
						&.menu-item-dashboard {
							order: 1;
						}
						&.menu-item-requests {
							order: 2;
						}
						&.menu-item-opportunities {
							order: 3;
						}
						&.menu-item-profile {
							order: 4;
						}
					}
				}
			}

			&:not(.mini) {
				.action-menu {
					.menu-item {
						> a {
							height: auto;
							padding-bottom: 14px;
						}
					}
				}
			}

			.admin-menu {
				position: fixed;
				top: 0;
				left: 0;
				width: 100%;
				padding-left: 65px;
				vertical-align: top;

				.admin-menu-wrap {
					background-color: @surfacePanel;
					border-bottom: 1px solid @borderRoseSubtle;
					height: 64px;
					padding-top: 11px;
					text-align: right;
				}

				.notifications {
					width: 20px;
					display: inline-block;
					vertical-align: top;
					margin: 9px 15px 0 0;
					cursor: pointer;
				}

				.book-call-header {
					margin-top: 6px;
				}

				.name-info {
					display: inline-block;
					vertical-align: top;
					padding: 9px 0 0;
					margin-right: 30px;
					position: relative;

					.brand-switch-help {
						animation: text_color_change 1s infinite alternate;
						background-color: @surfacePanel;
						color: @guidedRose;
						display: block;
						font-size: 16px;
						font-weight: @bold;
						margin: 0;
						padding: 2px 8px;
						position: absolute;
						right: 100%;
						top: 8px;
						white-space: nowrap;
					}

					svg,
					.svg {
						position: static;
						left: auto;
						top: auto;
						width: 18px;
						height: 18px;
					}

					.name {
						color: @darkContrast;
						font-size: 14px;
						display: inline-block;
						position: relative;
						padding-right: 16px;

						&:after {
							content: "";
							background: url("../assets/img/down-arrow.svg") center center no-repeat;
							position: absolute;
							width: 10px;
							height: 10px;
							right: 0;
							top: 50%;
							margin-top: -5px;
							transition: transform 0.35s ease-in-out;
						}

						&:hover {
							&:after {
								transform: rotate(360deg);
							}
						}
					}
				}

				.menu-item {
					text-align: center;
					margin: 0 15px 0 0;
					display: inline-block;

					> a,
					> span,
					.menu-link {
						.indent;
						display: inline-block;
						width: 34px;
						height: 34px;
						font-size: 12px;

						&:hover {
							background-color: transparent;
						}
					}

					&.help {
						margin-bottom: 0;
					}

					&.account {
						.initials {
							display: inline-block;
							background: none;
							background-color: @formDefaultColour;
							.rounded(@radiusPill);
							color: #fff;
							.opacity(1);
							text-indent: 0;
							text-align: center;
							width: 40px;
							height: 40px;
							padding: 0;
							font-size: 16px;
							display: inline-block;
							cursor: default;
							text-transform: uppercase;
							position: relative;
							overflow: hidden;

							span {
								display: block;
								padding: 12px 0 0;
								line-height: 1;
								color: inherit;
							}

							img {
								width: 100%;
								height: 100%;
								display: block;
								object-fit: contain;
							}

							&.is-logo {
								background-color: transparent;
								border: 1px solid fade(@guidedRose, 20%);
							}

							&.white {
								background-color: @guidedRose;
							}

							&.can-open-menu {
								cursor: pointer;
							}
						}

						> a,
						> span,
						.menu-link {
							svg,
							.svg {
								display: none;
							}

							.label {
								display: inline;
								font-size: 14px;
							}
						}
					}
				}
			}

			.menu-popout {
				position: absolute;
				background-color: @surfacePanel;
				border: 1px solid @borderRoseSubtle;
				right: 0;
				top: 46px;
				width: 220px;
				padding: 6px 12px 2px;
				.rounded(@radiusMd);
				display: none;
				.panelShadow;
				text-align: left;
				cursor: default;

				&.show {
					display: block;
				}

				&:after {
					content: "";
					position: absolute;
					width: 0;
					height: 0;
					box-sizing: border-box;
					border: 5px solid black;
					border-color: transparent transparent @surfacePanel @surfacePanel;
					-webkit-transform-origin: 0 0;
					transform-origin: 0 0;
					-webkit-transform: rotate(135deg);
					transform: rotate(135deg);
					box-shadow:
						-2px 3px 3px 0 rgba(0, 0, 0, 0.03),
						-1px 1px 0 0.5px rgba(0, 0, 0, 0.06);
					background-clip: padding-box;
					top: 0px;
					right: 7px;
				}

				hr {
					margin: 5px auto 8px;
					width: 50%;
				}

				.link-wrap {
					margin-bottom: 5px;

					&:last-child {
						margin-bottom: 0;
					}

					.svg {
						width: 15px;
						height: 15px;
						margin-top: -5px;
					}

					> a,
					> span,
					.menu-link {
						text-indent: 0;
						width: auto;
						display: inline-block;
						color: #000;
						padding: 0;
						height: auto;
						.opacity(0.8);
						cursor: pointer;
						.trans;
						text-decoration: none !important;

						&:hover {
							.opacity(1);
						}

						.label {
							padding: 0 0 0 6px;
							font-size: 14px;
							vertical-align: top;
							color: @guidedRose;
						}
					}
				}

				.brand-link-wrap {
					margin-bottom: 5px;

					> span,
					.menu-link {
						display: inline-block;
						padding: 0;
						width: auto;

						.label {
							color: @guidedRose;
							font-size: 14px;
							padding: 0 0 0 6px;
							vertical-align: top;
						}
					}

					.brand-menu-logo {
						height: 24px;
						left: auto;
						margin: -3px 2px 0 0;
						position: relative;
						top: auto;
						width: 24px;
					}
				}
			}
		}
	}

	body {
		.showMenu {
			.wrapper,
			.footer {
				display: block;
			}
		}
	}

	.fullMenu {
		header {
			width: @fullMenuWidth;

			.search {
				left: @fullMenuWidth + 22px;
			}

			.menu {
				.action-menu {
					.menu-item {
						width: calc(100% + 1px);

						> a,
						> span,
						.menu-link {
							width: auto;
							border-radius: @radiusMd 0 0 @radiusMd;

							.label {
								display: block;
							}
						}
					}
				}

				.admin-menu {
					padding-left: @fullMenuWidth - 2px;
				}
			}
		}
	}
}

@media screen and (max-width: 767px) {
	.header {
		.hide-mobile {
			display: none !important;
		}
	}
}

@media screen and (min-width: 768px) and (min-height: @minHeightDesktop) {
	.header {
		position: fixed;
		left: 0;

		.menu {
			.menu-item {
				&.minmax {
					position: absolute;
					bottom: 70px;
				}
			}
		}
	}
}

@media screen and (min-width: 768px) and (pointer: fine) {
	.header {
		.menu {
			&.mini {
				.menu-item {
					&:hover {
						> a,
						> span,
						.menu-link {
							.label {
								display: block;
							}
						}
					}
				}
			}
		}
	}
}

@media screen and (min-width: 1024px) {
	.header {
		.menu {
			.menu-popout {
				.link-wrap {
					> a,
					> span,
					.menu-link {
						display: block;
					}
				}
			}
		}
	}
}
</style>
